<template>
  <div class="widget">
    <h2 class="widget-title">Adgang til ledelsesinfo</h2>
    <div v-if="loading" class="widget-loader">
      <i class="fa fa-spinner"></i>
      Loading...
    </div>
    <div v-if="hasErrors">
      <ul class="errors">
        <li v-for="error in errors" v-bind:key="error" class="errors__error">{{ error }}</li>
      </ul>
    </div>
    <div v-if="!loading">
      <ul v-if="items && items.length" class="widget">
        <li v-for="(item, itemIndex) in items" v-bind:key="itemIndex" class="widget__item">

          <div v-if="item.links && item.links.length > 0">
            <ul class="widgetlist">
              <li
                v-for="(link, linkIndex) in item.links"
                v-bind:key="linkIndex"
                class="widgetlist__item"
                v-on:click="submitLink(widgetIdentifier + 'link' + linkIndex)"
                v-on:keypress="(e) => activateItem(e, 'link' + linkIndex)"
                :style="getIcon(link.iconFileName)"
                tabindex="0"
                role="button"
                :title="`Se ${link.linkText} i Meebook`"
              >
                <form
                  v-bind:action="getApiBaseUrl() + link.aulaApiEndpoint"
                  method="post"
                  target="_blank"
                  v-bind:ref="widgetIdentifier + 'link' + linkIndex"
                >
                  <input type="hidden" name="sessionUUID" v-bind:value="sessionUUID" />
                  <input type="hidden" :id="`aulaJWT${widgetIdentifier + 'link' + linkIndex}`" name="aulaJWT" value="" />
                  <input type="hidden" name="userProfile" v-bind:value="userProfileManager" />
                  <input type="hidden" name="currentWeekNumber" v-bind:value="getThisYearWeek()" />
                  <input v-for="(inst) in institutionFilter" type="hidden" name="institutionFilter[]" v-bind:value="inst" />
                </form>

                <div class="widgetlist__item__title">
                  <span>{{ link.linkText}}</span>
                </div>
              </li>
            </ul>
          </div>
          <div v-else>
            <p>Ingen info at vise</p>
          </div>
        </li>
      </ul>
      <div v-else-if="!hasErrors">
        <p>Ingen info at vise</p>
      </div>
    </div>
  </div>
</template>

<script>
  module.exports = {
    props: {
      axios: Function,
      moment: Function,
      getAulaToken: Function,
      sessionUUID: String,
      placement: String,
      currentWeekNumber: String,
      isMobileApp: Boolean,
      assuranceLevel: Number,
      // there is no "Manager" user profile value in Aula, only "Employee" that includes all teachers and managers
      // so we need to define our own user profile string for managers and it needs to bee in every
      // management widget
      userProfileManager:  {
        type: String,
        default: 'Manager',
      },
      widgetIdentifier:  {
        type: String,
        default: 'managementQuickLink-',
      },
      institutionFilter: [Array],
      group: String,
      meebookImgBaseUrl: {
        type: String,
        default: 'https://app.meebook.com/images/aula/',
      },
      meebookApiBaseUrl: String,
      meebookApiTestData: String
    },
    computed: {
      maxShown() {
        return this.showAll ? Infinity : 3;
      },
      hasErrors() {
        return this.errors && this.errors.length;
      },
    },
    methods: {
      getThisYearWeek() {
        this.moment.locale('da');
        return this.moment().format(this.moment.HTML5_FMT.WEEK);
      },
      getClient() {
        return this.axios.create({
          baseURL: this.getApiBaseUrl(),
          timeout: 30000,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json',
            'Authorization': 'Bearer ' + this.aulaToken,
            'sessionUUID': this.sessionUUID,
            'X-Version': '1.0'
          }
        });
      },
      getApiBaseUrl() {
        // production url (defined here because there will not be an API_BASE_URL on aula live)
        let url = 'https://app.meebook.com/aulaapi/';
        // if dev server is set, use that
        if (this.meebookApiBaseUrl !== undefined) {
          // could be localhost (or other local vhost) or development server
          url = this.meebookApiBaseUrl;
        }

        return url;
      },
      activateItem(e, index) {
        if (e.code === 'Enter' || e.code === 'Space') {
          this.submitLink(index);
        }
      },
      submitLink(index) {
        let aulaJwtInputElement = document.getElementById('aulaJWT' + index);
        // insert jwt token value into form
        aulaJwtInputElement.value = this.aulaToken;
        // send form
        this.$refs[index][0].submit();
        // reset aula jwt token input value to null
        aulaJwtInputElement.value = null;
      },
      getTestData() {
        // shold only be used when building html. Not "real" data
        this.loading = false;
        return JSON.parse(this.meebookApiTestData);
      },
      getIcon(iconName) {
        return `background-image: url(${this.meebookImgBaseUrl + iconName})`;
      },
      toggleShowAll() {
        this.showAll = !this.showAll;
      },
      loadContent() {
        // if we are just testing layout/styles we can use static test data
        if (this.meebookApiTestData !== undefined) {
          this.items = this.getTestData();
          return;
        }

        // call aula api for dynamic data
        let http = this.getClient();
        http.get(
          'managementquicklink/all',
          {
            params: {
              currentWeekNumber: this.getThisYearWeek(),
              userProfile: this.userProfileManager,
              institutionFilter: this.institutionFilter,
            }
          }
        ).then((response) => {

          this.loading = false;
          this.items = response.data || [];
        }).catch((error) => {
          if (error.response.status === 401) {
            // Aula Token expired
            this.aulaTokenExpired = true;
            this.loading = true;
            this.aulaToken = this.getAulaToken();
            this.errors.push('Din Aula-token fornyes.. Øjeblik');
          } else {
            this.loading = false;
            if (error.response.data.exceptionMessage) {
              this.errors.push(error.response.data.exceptionMessage);
            } else {
              this.errors.push('Ledelses info kunne ikke hentes.');
            }
          }
        });
      }
    },
    data: () => ({
      loading: true,
      items: '',
      errors: [],
      showAll: false,
      aulaToken: null,
      aulaTokenExpired: null
    }),
    mounted() {
      this.aulaToken = this.getAulaToken();
    },
    watch: {
      aulaToken: function () {
        if (this.aulaToken !== undefined && !this.aulaTokenExpired) {
          this.loadContent();
        } else if (this.aulaTokenExpired) {
          this.aulaTokenExpired = false;
          this.errors = [];
          this.loadContent();
        }
      }
    }
  }
</script>

<style scoped>
  .widget-loader{font-family:'Lato', 'Avenir', Helvetica, Arial, sans-serif;font-size:16px;margin:20px 0}.widget-loader>.fa{animation-name:spin;animation-duration:1s;animation-iteration-count:infinite;animation-timing-function:linear;animation-fill-mode:both;margin-right:10px}@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}.errors{margin-top:20px}.errors__error{display:block;padding:15px 20px;background-color:#fff;color:#b50050}*{box-sizing:border-box}ul{margin:0;padding:0;list-style-type:none}li{margin:0;width:100%}.widget-title{display:block;text-align:left;font-family:'Lato', 'Avenir', Helvetica, Arial, sans-serif;font-size:17px;font-weight:700;text-transform:uppercase;margin-bottom:0;margin-top:30px}.widget{text-align:left}.widget__item__name{display:block;font-size:14px;font-weight:700;text-transform:capitalize;color:#5D6C79;margin-top:30px;margin-bottom:10px}.widgetlist{text-align:left}.widgetlist__item{position:relative;padding:15px 20px;background-color:#fff;margin-bottom:5px;cursor:pointer;outline:0}.widgetlist__item:focus{background-color:rgba(255,255,255,0.4)}.widgetlist__item__title{font-size:16px;font-weight:700}.widgetlist__item__subtitle{font-size:11px;font-weight:700;color:#6A7681;text-transform:capitalize}.widgetlist__item__subtitle::first-letter{text-transform:uppercase}.widgetlist__showall{display:block;padding:0;margin-top:5px;font-size:14px;font-weight:700;text-decoration:underline;cursor:pointer;background-color:transparent;border:0;outline:none}.widgetlist__showall:focus{opacity:0.5}.widget{margin-top:20px}.widgetlist__item{padding:15px 20px 15px 70px;background-repeat:no-repeat;background-position:10px center;background-size:40px}
</style>
